import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { AtSign, KeyRound, ImageIcon, UploadCloud, Loader2 } from "lucide-react";
import { adminApi, setAdminToken } from "../../lib/adminApi";
import { assetUrl, formatApiErrorDetail } from "../../lib/api";
import HeroImageCropModal from "../../components/admin/HeroImageCropModal";
import ConfirmDialog from "../../components/admin/ConfirmDialog";

function SectionCard({ title, children, saving, onSave, testid, saveLabel = "Save" }) {
  return (
    <div className="hbe-card p-6 space-y-4" data-testid={testid}>
      <h2 className="font-serif text-xl text-hbe-primary">{title}</h2>
      <div className="space-y-4">{children}</div>
      {onSave && (
        <div>
          <button
            type="button"
            className="hbe-btn-primary"
            onClick={onSave}
            disabled={saving}
            data-testid={`${testid}-save`}
          >
            {saving ? "Saving…" : saveLabel}
          </button>
        </div>
      )}
    </div>
  );
}

function AccountEmailForm({ me, onUpdated }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!currentPassword || !newEmail) {
      toast.error("Enter your current password and the new email.");
      return;
    }
    if (newEmail.trim().toLowerCase() === (me?.email || "").toLowerCase()) {
      toast.error("That is already your email.");
      return;
    }
    setSaving(true);
    try {
      const r = await adminApi.patch("/admin/me", {
        current_password: currentPassword,
        new_email: newEmail.trim(),
      });
      setAdminToken(r.data.access_token);
      onUpdated?.(r.data.profile);
      setCurrentPassword("");
      setNewEmail("");
      toast.success("Email updated.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="hbe-card p-6 space-y-4" data-testid="settings-account-email">
      <div className="flex items-center gap-2">
        <AtSign size={16} className="text-hbe-accent" />
        <h3 className="font-serif text-lg text-hbe-primary">Change email</h3>
      </div>
      <p className="text-sm text-hbe-ink/60">
        Current: <span className="font-medium text-hbe-ink">{me?.email || "—"}</span>
      </p>
      <div>
        <label className="hbe-label">New email</label>
        <input
          type="email"
          autoComplete="email"
          className="hbe-input"
          value={newEmail}
          onChange={(e) => setNewEmail(e.target.value)}
          placeholder="you@yourdomain.com"
          data-testid="account-new-email"
        />
      </div>
      <div>
        <label className="hbe-label">Current password</label>
        <input
          type="password"
          autoComplete="current-password"
          className="hbe-input"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          data-testid="account-email-current-password"
        />
      </div>
      <div>
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          className="hbe-btn-primary"
          data-testid="account-email-save"
        >
          {saving ? "Updating…" : "Update email"}
        </button>
      </div>
    </div>
  );
}

function AccountPasswordForm() {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!currentPassword || !newPassword || !confirm) {
      toast.error("Fill every field.");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirm) {
      toast.error("New password and confirmation don't match.");
      return;
    }
    if (newPassword === currentPassword) {
      toast.error("New password must differ from the current one.");
      return;
    }
    setSaving(true);
    try {
      const r = await adminApi.patch("/admin/me", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      setAdminToken(r.data.access_token);
      setCurrentPassword("");
      setNewPassword("");
      setConfirm("");
      toast.success("Password updated.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="hbe-card p-6 space-y-4" data-testid="settings-account-password">
      <div className="flex items-center gap-2">
        <KeyRound size={16} className="text-hbe-accent" />
        <h3 className="font-serif text-lg text-hbe-primary">Change password</h3>
      </div>
      <div>
        <label className="hbe-label">Current password</label>
        <input
          type="password"
          autoComplete="current-password"
          className="hbe-input"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          data-testid="account-pw-current"
        />
      </div>
      <div>
        <label className="hbe-label">New password (min 8 chars)</label>
        <input
          type="password"
          autoComplete="new-password"
          className="hbe-input"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          data-testid="account-pw-new"
        />
      </div>
      <div>
        <label className="hbe-label">Confirm new password</label>
        <input
          type="password"
          autoComplete="new-password"
          className="hbe-input"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          data-testid="account-pw-confirm"
        />
      </div>
      <div>
        <button
          type="button"
          onClick={submit}
          disabled={saving}
          className="hbe-btn-primary"
          data-testid="account-pw-save"
        >
          {saving ? "Updating…" : "Update password"}
        </button>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [me, setMe] = useState(null);
  const [saving, setSaving] = useState({});
  const [heroUploading, setHeroUploading] = useState(false);
  const [heroCropOpen, setHeroCropOpen] = useState(false);
  const [confirmRemoveHero, setConfirmRemoveHero] = useState(false);
  const [heroHeadline, setHeroHeadline] = useState("");
  const [heroSubheadline, setHeroSubheadline] = useState("");
  const heroFileRef = useRef(null);

  useEffect(() => {
    adminApi
      .get("/admin/settings")
      .then((r) => {
        setSettings(r.data);
        setHeroHeadline(r.data?.hero_headline_override || "");
        setHeroSubheadline(r.data?.hero_subheadline_override || "");
      })
      .catch((e) => toast.error(formatApiErrorDetail(e)));
    adminApi
      .get("/admin/me")
      .then((r) => setMe(r.data))
      .catch(() => {});
  }, []);

  function set(k, v) {
    setSettings((s) => ({ ...s, [k]: v }));
  }

  async function saveSection(section, fields) {
    setSaving((s) => ({ ...s, [section]: true }));
    try {
      const patch = {};
      fields.forEach((f) => (patch[f] = settings[f]));
      const r = await adminApi.patch("/admin/settings", patch);
      setSettings(r.data);
      toast.success("Saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving((s) => ({ ...s, [section]: false }));
    }
  }

  async function onHeroSourcePicked(fileList) {
    if (!fileList || !fileList.length) return;
    const file = fileList[0];
    setHeroUploading(true);
    const fd = new FormData();
    fd.append("file", file, file.name);
    try {
      const r = await adminApi.post("/admin/settings/hero/source", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 0,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      setSettings(r.data);
      setHeroCropOpen(true);
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setHeroUploading(false);
    }
  }

  async function removeHero() {
    try {
      const r = await adminApi.delete("/admin/settings/hero");
      setSettings(r.data);
      toast.success("Hero image removed.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setConfirmRemoveHero(false);
    }
  }

  async function saveHeroCopy() {
    setSaving((s) => ({ ...s, heroCopy: true }));
    try {
      const r = await adminApi.patch("/admin/settings/hero-copy", {
        headline: heroHeadline,
        subheadline: heroSubheadline,
      });
      setSettings(r.data);
      setHeroHeadline(r.data?.hero_headline_override || "");
      setHeroSubheadline(r.data?.hero_subheadline_override || "");
      toast.success("Hero copy saved.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e));
    } finally {
      setSaving((s) => ({ ...s, heroCopy: false }));
    }
  }

  if (!settings) {
    return <div className="text-hbe-ink/50">Loading…</div>;
  }

  return (
    <div data-testid="admin-settings-page">
      <div>
        <div className="hbe-eyebrow">Configuration</div>
        <h1 className="mt-2 font-serif text-3xl md:text-4xl">Settings</h1>
      </div>

      {/* Home page hero — admin-controlled image + copy overrides */}
      <section className="mt-8 max-w-2xl" data-testid="settings-hero-section">
        <div className="mb-3">
          <h2 className="font-serif text-2xl text-hbe-primary">Home page hero</h2>
          <p className="mt-1 text-sm text-hbe-ink/60">
            The image visitors see first. Independent of your yacht photos —
            you control exactly what leads the site.
          </p>
        </div>

        <input
          ref={heroFileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
          hidden
          onChange={(e) => {
            onHeroSourcePicked(e.target.files);
            e.target.value = "";
          }}
          data-testid="hero-file-input"
        />

        <div className="hbe-card p-6 space-y-5">
          {!settings.hero_image_source_url ? (
            <button
              type="button"
              onClick={() => heroFileRef.current?.click()}
              className="w-full border-2 border-dashed border-hbe-line rounded-xl p-8 text-center bg-hbe-soft/30 hover:bg-hbe-soft/50 transition"
              disabled={heroUploading}
              data-testid="hero-empty-state"
            >
              {heroUploading ? (
                <Loader2 size={30} className="mx-auto text-hbe-accent animate-spin" />
              ) : (
                <UploadCloud size={30} className="mx-auto text-hbe-accent" />
              )}
              <div className="mt-3 text-sm font-medium text-hbe-primary">
                {heroUploading
                  ? "Uploading…"
                  : "Upload the image customers see first when they land on the site."}
              </div>
              <div className="mt-1 text-xs text-hbe-ink/55">
                This won't come from your yacht photos — you're in full
                control. JPG · PNG · WebP · HEIC up to 100 MB.
              </div>
            </button>
          ) : !settings.hero_image_url ? (
            <div
              className="flex items-center gap-4 border-l-4 border-hbe-accent bg-hbe-soft/40 rounded-xl p-4"
              data-testid="hero-awaiting-crop"
            >
              <img
                src={assetUrl(settings.hero_image_source_url)}
                alt=""
                className="w-28 h-16 rounded-md object-cover border border-hbe-line"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-hbe-primary">
                  Set hero crop
                </div>
                <div className="text-xs text-hbe-ink/60 mt-0.5">
                  Position the shot before it goes live.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setHeroCropOpen(true)}
                className="hbe-btn-primary !py-2 !text-[0.85rem] !min-h-[36px]"
                data-testid="hero-open-crop"
              >
                Position
              </button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="hero-active">
              <div className="w-full aspect-video rounded-xl overflow-hidden bg-hbe-soft border border-hbe-line">
                <img
                  src={assetUrl(settings.hero_image_url)}
                  alt="Home hero preview"
                  className="w-full h-full object-cover"
                  data-testid="hero-preview-image"
                />
              </div>
              <p className="text-xs text-hbe-ink/60">
                This is the exact crop visitors see at the top of the home page.
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setHeroCropOpen(true)}
                  className="hbe-btn-primary !py-2 !text-[0.85rem] !min-h-[36px]"
                  data-testid="hero-reposition"
                >
                  Reposition
                </button>
                <button
                  type="button"
                  onClick={() => heroFileRef.current?.click()}
                  disabled={heroUploading}
                  className="hbe-btn-ghost !py-2 !text-[0.85rem] !min-h-[36px]"
                  data-testid="hero-replace"
                >
                  {heroUploading ? "Uploading…" : "Replace source"}
                </button>
              </div>
              <button
                type="button"
                onClick={() => setConfirmRemoveHero(true)}
                className="text-xs text-red-600 hover:underline"
                data-testid="hero-remove"
              >
                Remove hero image
              </button>
            </div>
          )}
        </div>

        <div
          className="hbe-card p-6 space-y-4 mt-4"
          data-testid="settings-hero-copy"
        >
          <h3 className="font-serif text-lg text-hbe-primary">Hero copy</h3>
          <div>
            <label className="hbe-label">Hero headline (optional)</label>
            <input
              className="hbe-input"
              value={heroHeadline}
              onChange={(e) => setHeroHeadline(e.target.value)}
              placeholder="Private yacht experiences in Miami."
              data-testid="hero-headline-input"
            />
            <p className="mt-1 text-xs text-hbe-ink/55">
              Leave blank to use the default.
            </p>
          </div>
          <div>
            <label className="hbe-label">Hero subheadline (optional)</label>
            <input
              className="hbe-input"
              value={heroSubheadline}
              onChange={(e) => setHeroSubheadline(e.target.value)}
              placeholder="A private yacht, your people, your time."
              data-testid="hero-subheadline-input"
            />
          </div>
          <div>
            <button
              type="button"
              className="hbe-btn-primary"
              onClick={saveHeroCopy}
              disabled={saving.heroCopy}
              data-testid="hero-copy-save"
            >
              {saving.heroCopy ? "Saving…" : "Save copy"}
            </button>
          </div>
        </div>
      </section>

      {/* Account (self-management) */}
      <section className="mt-8 max-w-2xl" data-testid="settings-account-section">
        <div className="mb-3">
          <h2 className="font-serif text-2xl text-hbe-primary">Account</h2>
          <p className="mt-1 text-sm text-hbe-ink/60">
            Update the login for this admin. You'll stay signed in — a fresh
            session token is issued when you save.
          </p>
        </div>
        <div className="grid gap-6">
          <AccountEmailForm me={me} onUpdated={setMe} />
          <AccountPasswordForm />
        </div>
      </section>

      <div className="mt-10 grid gap-6 max-w-2xl">
        <SectionCard
          title="Contact"
          saving={saving.contact}
          onSave={() => saveSection("contact", ["public_phone", "public_whatsapp", "public_email"])}
          testid="settings-contact"
        >
          <div>
            <label className="hbe-label">Public phone</label>
            <input
              className="hbe-input"
              value={settings.public_phone || ""}
              onChange={(e) => set("public_phone", e.target.value)}
              placeholder="+1 305 555 0123"
              data-testid="settings-public-phone"
            />
          </div>
          <div>
            <label className="hbe-label">Public WhatsApp</label>
            <input
              className="hbe-input"
              value={settings.public_whatsapp || ""}
              onChange={(e) => set("public_whatsapp", e.target.value)}
              placeholder="+1 305 555 0123"
              data-testid="settings-public-whatsapp"
            />
          </div>
          <div>
            <label className="hbe-label">Public email</label>
            <input
              type="email"
              className="hbe-input"
              value={settings.public_email || ""}
              onChange={(e) => set("public_email", e.target.value)}
              placeholder="hello@hbe.example"
              data-testid="settings-public-email"
            />
          </div>
          <p className="text-xs text-hbe-ink/55">
            These appear on the public site immediately.
          </p>
        </SectionCard>

        <SectionCard
          title="Deposit"
          saving={saving.deposit}
          onSave={() => saveSection("deposit", ["deposit_amount_cents", "deposit_currency", "deposit_is_refundable"])}
          testid="settings-deposit"
        >
          <div>
            <label className="hbe-label">Deposit amount ($)</label>
            <input
              type="number"
              min={5}
              max={5000}
              step={1}
              className="hbe-input"
              value={Math.round((settings.deposit_amount_cents ?? 50000) / 100)}
              onChange={(e) => set("deposit_amount_cents", Math.max(50, Math.round(Number(e.target.value || 0) * 100)))}
              data-testid="settings-deposit-amount"
            />
            <p className="mt-1 text-xs text-hbe-ink/55">
              Charged via Stripe at checkout. Balance is collected offline on
              the charter day. New bookings use this amount; existing bookings
              keep the deposit they were quoted.
            </p>
          </div>
          <div>
            <label className="hbe-label">Currency</label>
            <input
              className="hbe-input uppercase"
              maxLength={3}
              value={(settings.deposit_currency || "usd").toUpperCase()}
              onChange={(e) => set("deposit_currency", e.target.value.toLowerCase())}
              data-testid="settings-deposit-currency"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!settings.deposit_is_refundable}
              onChange={(e) => set("deposit_is_refundable", e.target.checked)}
              data-testid="settings-deposit-refundable"
            />
            Deposit is refundable
            <span className="text-xs text-hbe-ink/55">
              (leave off — public copy says non-refundable)
            </span>
          </label>
        </SectionCard>

        <SectionCard
          title="Tax"
          saving={saving.tax}
          onSave={() => saveSection("tax", ["tax_enabled", "tax_rate_percent", "tax_label"])}
          testid="settings-tax"
        >
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!settings.tax_enabled}
              onChange={(e) => set("tax_enabled", e.target.checked)}
              data-testid="settings-tax-enabled"
            />
            Enable tax on public checkout
          </label>
          <div>
            <label className="hbe-label">Rate (%)</label>
            <input
              type="number"
              min={0}
              max={100}
              step={0.01}
              className="hbe-input"
              value={settings.tax_rate_percent ?? 0}
              onChange={(e) => set("tax_rate_percent", Number(e.target.value))}
              data-testid="settings-tax-rate"
            />
          </div>
          <div>
            <label className="hbe-label">Label</label>
            <input
              className="hbe-input"
              value={settings.tax_label || ""}
              onChange={(e) => set("tax_label", e.target.value)}
              data-testid="settings-tax-label"
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Email (used by future Stripe emails)"
          saving={saving.email}
          onSave={() => saveSection("email", ["email_from", "email_reply_to"])}
          testid="settings-email"
        >
          <div>
            <label className="hbe-label">From address</label>
            <input
              type="email"
              className="hbe-input"
              value={settings.email_from || ""}
              onChange={(e) => set("email_from", e.target.value)}
              placeholder="reservations@hbe.example"
              data-testid="settings-email-from"
            />
          </div>
          <div>
            <label className="hbe-label">Reply-to</label>
            <input
              type="email"
              className="hbe-input"
              value={settings.email_reply_to || ""}
              onChange={(e) => set("email_reply_to", e.target.value)}
              placeholder="hello@hbe.example"
              data-testid="settings-email-reply-to"
            />
          </div>
        </SectionCard>
      </div>

      <HeroImageCropModal
        open={heroCropOpen}
        settings={settings}
        onClose={() => setHeroCropOpen(false)}
        onSaved={(updated) => setSettings(updated)}
      />
      <ConfirmDialog
        open={confirmRemoveHero}
        onClose={() => setConfirmRemoveHero(false)}
        onConfirm={removeHero}
        title="Remove hero image?"
        message="The home page will fall back to a solid dark-navy hero with the default headline. You can upload a new hero anytime."
        confirmLabel="Remove"
        danger
      />
    </div>
  );
}
